import React, { useMemo, useState } from 'react';
import { MessageSquare, Download, Search, ArrowLeft, Calendar, Clock, Video, FileText, CreditCard, CheckCircle2, AlertTriangle, MoreVertical, Paperclip, User as UserIcon, ChevronRight, XCircle, AlertOctagon, RefreshCcw, Sparkles } from 'lucide-react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Input } from '../ui/Input';
import { Pagination } from '../ui/Pagination';
import { useToast } from '../ui/Toast';
import { ZodiacTheme } from './zodiacThemes';
interface OrdersTabProps {
  theme: ZodiacTheme;
  onNavigate: (page: string) => void;
  setActiveTab: (tab: string) => void;
}
type OrderStatus = 'new' | 'in_progress' | 'waiting_data' | 'waiting_consultation' | 'completed' | 'cancelled' | 'dispute' | 'refunded';
type PaymentStatus = 'paid' | 'pending' | 'refunded';
type OrderFormat = 'video' | 'audio' | 'chat' | 'text';
interface OrderHistoryItem {
  status: OrderStatus;
  date: string;
  description: string;
}
interface OrderFile {
  id: string;
  name: string;
  size: string;
  type: 'pdf' | 'image' | 'doc';
}
interface Order {
  id: string;
  serviceName: string;
  astrologerName: string;
  astrologerInitials: string;
  date: string;
  price: number;
  status: OrderStatus;
  paymentStatus: PaymentStatus;
  format: OrderFormat;
  deadline?: string;
  hasReport: boolean;
  hasConsultation: boolean;
  history: OrderHistoryItem[];
  files: OrderFile[];
  notes?: string;
}
const MOCK_ORDERS: Order[] = [{
  id: 'ORD-1042',
  serviceName: 'Консультация: Синастрия',
  astrologerName: 'Елена Смирнова',
  astrologerInitials: 'ЕС',
  date: '12 Окт 2024, 14:30',
  price: 5000,
  status: 'in_progress',
  paymentStatus: 'paid',
  format: 'video',
  deadline: '15 Окт 2024',
  hasReport: false,
  hasConsultation: true,
  history: [{
    status: 'new',
    date: '12 Окт 2024, 14:30',
    description: 'Заказ создан'
  }, {
    status: 'in_progress',
    date: '12 Окт 2024, 15:00',
    description: 'Астролог взял в работу'
  }],
  files: [],
  notes: 'Пожалуйста, подготовьте точное время рождения обоих партнеров.'
}, {
  id: 'ORD-0984',
  serviceName: 'Натальная карта (Полный разбор)',
  astrologerName: 'Анна Коваль',
  astrologerInitials: 'АК',
  date: '25 Сен 2024, 10:15',
  price: 3500,
  status: 'completed',
  paymentStatus: 'paid',
  format: 'text',
  hasReport: true,
  hasConsultation: false,
  history: [{
    status: 'new',
    date: '25 Сен 2024, 10:15',
    description: 'Заказ создан'
  }, {
    status: 'in_progress',
    date: '25 Сен 2024, 11:00',
    description: 'В работе'
  }, {
    status: 'completed',
    date: '28 Сен 2024, 16:45',
    description: 'Отчёт готов'
  }],
  files: [{
    id: 'f1',
    name: 'Натальная_карта_Анна.pdf',
    size: '2.4 MB',
    type: 'pdf'
  }]
}, {
  id: 'ORD-1055',
  serviceName: 'Соляр на 2025 год',
  astrologerName: 'Дмитрий Волков',
  astrologerInitials: 'ДВ',
  date: '14 Окт 2024, 09:00',
  price: 4000,
  status: 'waiting_data',
  paymentStatus: 'pending',
  format: 'text',
  hasReport: true,
  hasConsultation: false,
  history: [{
    status: 'new',
    date: '14 Окт 2024, 09:00',
    description: 'Заказ создан'
  }, {
    status: 'waiting_data',
    date: '14 Окт 2024, 09:05',
    description: 'Ожидание данных от клиента'
  }],
  files: [],
  notes: 'Необходимо заполнить анкету с местом встречи соляра.'
}, {
  id: 'ORD-1012',
  serviceName: 'Экспресс-ответ на вопрос',
  astrologerName: 'Мария Петрова',
  astrologerInitials: 'МП',
  date: '05 Окт 2024, 18:20',
  price: 1500,
  status: 'waiting_consultation',
  paymentStatus: 'paid',
  format: 'chat',
  deadline: '06 Окт 2024',
  hasReport: false,
  hasConsultation: true,
  history: [{
    status: 'new',
    date: '05 Окт 2024, 18:20',
    description: 'Заказ создан'
  }, {
    status: 'in_progress',
    date: '05 Окт 2024, 18:30',
    description: 'В работе'
  }, {
    status: 'waiting_consultation',
    date: '05 Окт 2024, 19:00',
    description: 'Ожидание начала чата'
  }],
  files: []
}, {
  id: 'ORD-0950',
  serviceName: 'Детский гороскоп',
  astrologerName: 'Елена Смирнова',
  astrologerInitials: 'ЕС',
  date: '10 Сен 2024, 11:00',
  price: 4500,
  status: 'cancelled',
  paymentStatus: 'refunded',
  format: 'text',
  hasReport: true,
  hasConsultation: false,
  history: [{
    status: 'new',
    date: '10 Сен 2024, 11:00',
    description: 'Заказ создан'
  }, {
    status: 'cancelled',
    date: '11 Сен 2024, 10:00',
    description: 'Отменен клиентом'
  }],
  files: []
}, {
  id: 'ORD-0921',
  serviceName: 'Бизнес-прогноз на месяц',
  astrologerName: 'Игорь Николаев',
  astrologerInitials: 'ИН',
  date: '01 Сен 2024, 15:45',
  price: 8000,
  status: 'dispute',
  paymentStatus: 'paid',
  format: 'video',
  hasReport: true,
  hasConsultation: true,
  history: [{
    status: 'new',
    date: '01 Сен 2024, 15:45',
    description: 'Заказ создан'
  }, {
    status: 'in_progress',
    date: '02 Сен 2024, 10:00',
    description: 'В работе'
  }, {
    status: 'completed',
    date: '05 Сен 2024, 14:00',
    description: 'Заказ сдан'
  }, {
    status: 'dispute',
    date: '06 Сен 2024, 09:30',
    description: 'Открыт спор'
  }],
  files: [{
    id: 'f2',
    name: 'Бизнес_прогноз_Сентябрь.pdf',
    size: '5.1 MB',
    type: 'pdf'
  }],
  notes: 'Клиент не согласен с результатами прогноза.'
}];
const STATUS_CONFIG: Record<OrderStatus, {
  label: string;
  color: string;
  bg: string;
  icon: React.ReactNode;
}> = {
  new: {
    label: 'Новый',
    color: 'text-blue-700',
    bg: 'bg-blue-100',
    icon: <Sparkles className="w-4 h-4" data-id="element-18531" />
  },
  in_progress: {
    label: 'В работе',
    color: 'text-amber-700',
    bg: 'bg-amber-100',
    icon: <RefreshCcw className="w-4 h-4" data-id="element-18532" />
  },
  waiting_data: {
    label: 'Ожидает данных',
    color: 'text-orange-700',
    bg: 'bg-orange-100',
    icon: <AlertTriangle className="w-4 h-4" data-id="element-18533" />
  },
  waiting_consultation: {
    label: 'Ожидает сессии',
    color: 'text-purple-700',
    bg: 'bg-purple-100',
    icon: <Clock className="w-4 h-4" data-id="element-18534" />
  },
  completed: {
    label: 'Завершён',
    color: 'text-emerald-700',
    bg: 'bg-emerald-100',
    icon: <CheckCircle2 className="w-4 h-4" data-id="element-18535" />
  },
  cancelled: {
    label: 'Отменён',
    color: 'text-slate-700',
    bg: 'bg-slate-100',
    icon: <XCircle className="w-4 h-4" data-id="element-18536" />
  },
  dispute: {
    label: 'Спор',
    color: 'text-rose-700',
    bg: 'bg-rose-100',
    icon: <AlertOctagon className="w-4 h-4" data-id="element-18537" />
  },
  refunded: {
    label: 'Возврат',
    color: 'text-slate-700',
    bg: 'bg-slate-100',
    icon: <RefreshCcw className="w-4 h-4" data-id="element-18538" />
  }
};
const FORMAT_CONFIG: Record<OrderFormat, {
  label: string;
  icon: React.ReactNode;
}> = {
  video: {
    label: 'Видеозвонок',
    icon: <Video className="w-4 h-4" data-id="element-18539" />
  },
  audio: {
    label: 'Аудиозвонок',
    icon: <MessageSquare className="w-4 h-4" data-id="element-18540" />
  },
  chat: {
    label: 'Текстовый чат',
    icon: <MessageSquare className="w-4 h-4" data-id="element-18541" />
  },
  text: {
    label: 'Письменный отчёт',
    icon: <FileText className="w-4 h-4" data-id="element-18542" />
  }
};
type FilterType = 'all' | 'active' | 'completed' | 'cancelled' | 'with_report' | 'with_consultation';
const FILTERS: {
  id: FilterType;
  label: string;
}[] = [{
  id: 'all',
  label: 'Все заказы'
}, {
  id: 'active',
  label: 'Активные'
}, {
  id: 'completed',
  label: 'Завершенные'
}, {
  id: 'cancelled',
  label: 'Отмененные'
}, {
  id: 'with_report',
  label: 'С отчётом'
}, {
  id: 'with_consultation',
  label: 'С консультацией'
}];
const ITEMS_PER_PAGE = 5;
export function OrdersTab({
  theme,
  onNavigate,
  setActiveTab
}: OrdersTabProps) {
  const {
    success,
    error
  } = useToast();
  // State
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  // Filtering and Search
  const filteredOrders = useMemo(() => {
    let result = MOCK_ORDERS;
    // Search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(o => o.id.toLowerCase().includes(query) || o.serviceName.toLowerCase().includes(query) || o.astrologerName.toLowerCase().includes(query));
    }
    // Filters
    switch (activeFilter) {
      case 'active':
        result = result.filter(o => ['new', 'in_progress', 'waiting_data', 'waiting_consultation'].includes(o.status));
        break;
      case 'completed':
        result = result.filter(o => o.status === 'completed');
        break;
      case 'cancelled':
        result = result.filter(o => ['cancelled', 'refunded'].includes(o.status));
        break;
      case 'with_report':
        result = result.filter(o => o.hasReport);
        break;
      case 'with_consultation':
        result = result.filter(o => o.hasConsultation);
        break;
    }
    return result;
  }, [searchQuery, activeFilter]);
  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / ITEMS_PER_PAGE);
  const paginatedOrders = filteredOrders.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  // Handlers
  const handleDownload = (fileName: string) => {
    success(`Скачивание файла ${fileName} начато`);
  };
  const handleCancelOrder = () => {
    success('Запрос на отмену отправлен');
  };
  const handleOpenDispute = () => {
    error('Спор открыт. Поддержка свяжется с вами.');
  };
  // Render Detail View
  if (selectedOrderId) {
    const order = MOCK_ORDERS.find(o => o.id === selectedOrderId);
    if (!order) return null;
    const statusConfig = STATUS_CONFIG[order.status];
    const formatConfig = FORMAT_CONFIG[order.format];
    return <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-300" data-id="element-18543">
        {/* Detail Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6" data-id="element-18544">
          <div className="flex items-center gap-4" data-id="element-18545">
            <Button variant="ghost" size="sm" className="p-2 -ml-2" onClick={() => setSelectedOrderId(null)} data-id="element-18546">
              <ArrowLeft className="w-5 h-5" data-id="element-18547" />
            </Button>
            <div data-id="element-18548">
              <div className="flex items-center gap-3" data-id="element-18549">
                <h2 className="text-2xl font-bold text-slate-900" data-id="element-18550">
                  Заказ {order.id}
                </h2>
                <span className={`px-2.5 py-1 rounded-full text-xs font-bold flex items-center gap-1.5 ${statusConfig.bg} ${statusConfig.color}`} data-id="element-18551">
                  {statusConfig.icon}
                  {statusConfig.label}
                </span>
              </div>
              <div className="text-sm text-slate-500 mt-1" data-id="element-18552">
                Оформлен {order.date}
              </div>
            </div>
          </div>
          <div className="flex gap-2" data-id="element-18553">
            <Button variant="outline" size="sm" icon={<MessageSquare className="w-4 h-4" data-id="element-18555" />} onClick={() => setActiveTab('chat')} data-id="element-18554">
              Чат с астрологом
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-18556">
          {/* Main Content (Left Column) */}
          <div className="lg:col-span-2 space-y-6" data-id="element-18557">
            {/* Order Info Card */}
            <Card padding="lg" data-id="element-18558">
              <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-18559">
                Информация о заказе
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-18560">
                <div data-id="element-18561">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-18562">Услуга</div>
                  <div className="font-medium text-slate-900" data-id="element-18563">
                    {order.serviceName}
                  </div>
                </div>
                <div data-id="element-18564">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-18565">Формат</div>
                  <div className="font-medium text-slate-900 flex items-center gap-2" data-id="element-18566">
                    {formatConfig.icon}
                    {formatConfig.label}
                  </div>
                </div>
                <div data-id="element-18567">
                  <div className="text-sm text-slate-500 mb-1" data-id="element-18568">Стоимость</div>
                  <div className="font-medium text-slate-900" data-id="element-18569">
                    {order.price.toLocaleString('ru-RU')} ₽
                  </div>
                </div>
                {order.deadline && <div data-id="element-18570">
                    <div className="text-sm text-slate-500 mb-1" data-id="element-18571">Дедлайн</div>
                    <div className="font-medium text-slate-900 flex items-center gap-2" data-id="element-18572">
                      <Clock className="w-4 h-4 text-amber-500" data-id="element-18573" />
                      {order.deadline}
                    </div>
                  </div>}
              </div>

              {order.notes && <div className="mt-6 pt-6 border-t border-slate-100" data-id="element-18574">
                  <div className="text-sm text-slate-500 mb-2" data-id="element-18575">
                    Заметки к заказу
                  </div>
                  <div className="bg-slate-50 p-4 rounded-xl text-sm text-slate-700" data-id="element-18576">
                    {order.notes}
                  </div>
                </div>}
            </Card>

            {/* Astrologer Card */}
            <Card padding="md" className="flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-18577">
              <div className="flex items-center gap-4" data-id="element-18578">
                <div className={`w-12 h-12 rounded-full ${theme.bg} ${theme.text} flex items-center justify-center font-bold text-lg`} data-id="element-18579">
                  {order.astrologerInitials}
                </div>
                <div data-id="element-18580">
                  <div className="text-sm text-slate-500" data-id="element-18581">Исполнитель</div>
                  <div className="font-bold text-slate-900" data-id="element-18582">
                    {order.astrologerName}
                  </div>
                </div>
              </div>
              <Button variant="outline" size="sm" onClick={() => onNavigate('astrologer-profile')} data-id="element-18583">
                Профиль астролога
              </Button>
            </Card>

            {/* Files/Report Section */}
            {order.files.length > 0 && <Card padding="lg" data-id="element-18584">
                <h3 className="text-lg font-bold text-slate-900 mb-4 flex items-center gap-2" data-id="element-18585">
                  <Paperclip className="w-5 h-5 text-slate-400" data-id="element-18586" />
                  Файлы и отчёты
                </h3>
                <div className="space-y-3" data-id="element-18587">
                  {order.files.map(file => <div key={file.id} className="flex items-center justify-between p-3 border border-slate-100 rounded-xl hover:border-slate-200 transition-colors" data-id="element-18588">
                      <div className="flex items-center gap-3" data-id="element-18589">
                        <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600" data-id="element-18590">
                          <FileText className="w-5 h-5" data-id="element-18591" />
                        </div>
                        <div data-id="element-18592">
                          <div className="font-medium text-slate-900 text-sm" data-id="element-18593">
                            {file.name}
                          </div>
                          <div className="text-xs text-slate-500" data-id="element-18594">
                            {file.size} • {file.type.toUpperCase()}
                          </div>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" icon={<Download className="w-4 h-4" data-id="element-18596" />} onClick={() => handleDownload(file.name)} data-id="element-18595">
                        Скачать
                      </Button>
                    </div>)}
                </div>
              </Card>}
          </div>

          {/* Sidebar (Right Column) */}
          <div className="space-y-6" data-id="element-18597">
            {/* Status History */}
            <Card padding="lg" data-id="element-18598">
              <h3 className="text-lg font-bold text-slate-900 mb-6" data-id="element-18599">
                История заказа
              </h3>
              <div className="relative pl-4 border-l-2 border-slate-100 space-y-6" data-id="element-18600">
                {order.history.map((item, index) => {
                const isLast = index === order.history.length - 1;
                const itemStatusConfig = STATUS_CONFIG[item.status];
                return <div key={index} className="relative" data-id="element-18601">
                      <div className={`absolute -left-[21px] w-3 h-3 rounded-full border-2 border-white ${isLast ? itemStatusConfig.bg.replace('bg-', 'bg-').replace('100', '500') : 'bg-slate-300'}`} data-id="element-18602" />
                      <div className="mb-1" data-id="element-18603">
                        <span className={`text-sm font-bold ${isLast ? 'text-slate-900' : 'text-slate-500'}`} data-id="element-18604">
                          {itemStatusConfig.label}
                        </span>
                      </div>
                      <div className="text-xs text-slate-500 mb-1" data-id="element-18605">
                        {item.date}
                      </div>
                      <div className="text-sm text-slate-600" data-id="element-18606">
                        {item.description}
                      </div>
                    </div>;
              })}
              </div>
            </Card>

            {/* Payment Info */}
            <Card padding="lg" data-id="element-18607">
              <h3 className="text-lg font-bold text-slate-900 mb-4" data-id="element-18608">Оплата</h3>
              <div className="flex items-center justify-between mb-4" data-id="element-18609">
                <span className="text-slate-600" data-id="element-18610">Статус</span>
                {order.paymentStatus === 'paid' ? <Badge variant="success" data-id="element-18611">Оплачено</Badge> : order.paymentStatus === 'pending' ? <Badge variant="warning" data-id="element-18612">Ожидает оплаты</Badge> : <Badge variant="neutral" data-id="element-18613">Возврат</Badge>}
              </div>
              <div className="flex items-center justify-between font-bold text-slate-900 pt-4 border-t border-slate-100" data-id="element-18614">
                <span data-id="element-18615">Итого</span>
                <span data-id="element-18616">{order.price.toLocaleString('ru-RU')} ₽</span>
              </div>
            </Card>

            {/* Actions */}
            <div className="space-y-3" data-id="element-18617">
              {['new', 'in_progress', 'waiting_data', 'waiting_consultation'].includes(order.status) && <Button variant="outline" fullWidth className="text-rose-600 border-rose-200 hover:bg-rose-50" onClick={handleCancelOrder} data-id="element-18618">
                  Отменить заказ
                </Button>}
              {['completed'].includes(order.status) && <Button variant="ghost" fullWidth className="text-slate-500 hover:text-rose-600 hover:bg-rose-50" onClick={handleOpenDispute} data-id="element-18619">
                  Открыть спор
                </Button>}
            </div>
          </div>
        </div>
      </div>;
  }
  // Render List View
  return <div className="space-y-6" data-id="element-18620">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-18621">
        <h2 className="text-2xl font-bold text-slate-900" data-id="element-18622">Мои заказы</h2>
        <Button onClick={() => onNavigate('astrologers')} data-id="element-18623">
          Найти астролога
        </Button>
      </div>

      {/* Filters and Search */}
      <div className="flex flex-col md:flex-row gap-4" data-id="element-18624">
        <div className="w-full md:w-72 flex-shrink-0" data-id="element-18625">
          <Input placeholder="Поиск по ID, услуге, астрологу..." value={searchQuery} onChange={e => {
          setSearchQuery(e.target.value);
          setCurrentPage(1);
        }} icon={<Search className="w-5 h-5 text-slate-400" data-id="element-18627" />} data-id="element-18626" />
        </div>
        <div className="flex-1 overflow-x-auto pb-2 -mb-2 scrollbar-hide" data-id="element-18628">
          <div className="flex gap-2" data-id="element-18629">
            {FILTERS.map(filter => <button key={filter.id} onClick={() => {
            setActiveFilter(filter.id);
            setCurrentPage(1);
          }} className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors ${activeFilter === filter.id ? `${theme.bg} ${theme.text} ring-1 ${theme.ring}` : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-id="element-18630">
                {filter.label}
              </button>)}
          </div>
        </div>
      </div>

      {/* Orders List */}
      {paginatedOrders.length > 0 ? <div className="space-y-4" data-id="element-18631">
          {paginatedOrders.map(order => {
        const statusConfig = STATUS_CONFIG[order.status];
        const formatConfig = FORMAT_CONFIG[order.format];
        return <Card key={order.id} padding="md" className={`border-l-4 transition-all hover:shadow-md cursor-pointer ${order.status === 'completed' ? 'border-l-emerald-500' : order.status === 'cancelled' || order.status === 'refunded' ? 'border-l-slate-300 opacity-75' : order.status === 'dispute' ? 'border-l-rose-500' : `border-l-${theme.text.split('-')[1]}-500` // Use theme color for active
        }`} onClick={() => setSelectedOrderId(order.id)} data-id="element-18632">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4" data-id="element-18633">
                  <div className="flex items-start gap-4 flex-1 min-w-0" data-id="element-18634">
                    <div className={`w-12 h-12 rounded-full ${theme.bg} ${theme.text} flex items-center justify-center font-bold flex-shrink-0`} data-id="element-18635">
                      {order.astrologerInitials}
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-18636">
                      <div className="flex flex-wrap items-center gap-2 mb-1" data-id="element-18637">
                        <span className="text-sm font-mono text-slate-500" data-id="element-18638">
                          {order.id}
                        </span>
                        <span className="text-sm text-slate-400" data-id="element-18639">•</span>
                        <span className="text-sm text-slate-500" data-id="element-18640">
                          {order.date}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 ${statusConfig.bg} ${statusConfig.color}`} data-id="element-18641">
                          {statusConfig.label}
                        </span>
                        {order.paymentStatus === 'paid' && <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-600 border border-emerald-100" data-id="element-18642">
                            Оплачено
                          </span>}
                      </div>
                      <h3 className="font-bold text-slate-900 text-lg truncate mb-1" data-id="element-18643">
                        {order.serviceName}
                      </h3>
                      <div className="flex flex-wrap items-center gap-3 text-sm text-slate-600" data-id="element-18644">
                        <span className="flex items-center gap-1" data-id="element-18645">
                          <UserIcon className="w-4 h-4" data-id="element-18646" />
                          {order.astrologerName}
                        </span>
                        <span className="flex items-center gap-1" data-id="element-18647">
                          {formatConfig.icon}
                          {formatConfig.label}
                        </span>
                        <span className="font-medium text-slate-900" data-id="element-18648">
                          {order.price.toLocaleString('ru-RU')} ₽
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-2 w-full md:w-auto mt-2 md:mt-0 pt-4 md:pt-0 border-t md:border-t-0 border-slate-100" data-id="element-18649">
                    {order.hasReport && order.status === 'completed' && <Button variant="outline" size="sm" className="w-full sm:w-auto" icon={<Download className="w-4 h-4" data-id="element-18651" />} onClick={e => {
                e.stopPropagation();
                handleDownload('Отчет.pdf');
              }} data-id="element-18650">
                        Скачать
                      </Button>}
                    <Button variant="outline" size="sm" className="w-full sm:w-auto" icon={<MessageSquare className="w-4 h-4" data-id="element-18653" />} onClick={e => {
                e.stopPropagation();
                setActiveTab('chat');
              }} data-id="element-18652">
                      Чат
                    </Button>
                    <Button variant="ghost" size="sm" className="w-full sm:w-auto text-slate-500 hover:text-slate-900" icon={<ChevronRight className="w-4 h-4" data-id="element-18655" />} data-id="element-18654">
                      Детали
                    </Button>
                  </div>
                </div>
              </Card>;
      })}
        </div> : <Card padding="xl" className="flex flex-col items-center justify-center text-center" data-id="element-18656">
          <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4" data-id="element-18657">
            <Search className="w-8 h-8 text-slate-400" data-id="element-18658" />
          </div>
          <h3 className="text-xl font-bold text-slate-900 mb-2" data-id="element-18659">
            Заказы не найдены
          </h3>
          <p className="text-slate-500 max-w-md mb-6" data-id="element-18660">
            По вашему запросу или выбранным фильтрам ничего не найдено.
            Попробуйте изменить параметры поиска.
          </p>
          <Button variant="outline" onClick={() => {
        setSearchQuery('');
        setActiveFilter('all');
      }} data-id="element-18661">
            Сбросить фильтры
          </Button>
        </Card>}

      {/* Pagination */}
      {totalPages > 1 && <div className="mt-8 flex justify-center" data-id="element-18662">
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} data-id="element-18663" />
        </div>}
    </div>;
}